import { type Editor, EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { useEffect, useMemo, useRef } from 'react';
import InnerHTMLDiv from '../common/inner-html-div.component';
import RequiredIndicator from '../common/required-indicator/required-indicator.component';
import type { DynamicFormRequiredType } from '../dynamic-form/dynamic-form.enums';
import EmptyFieldLabel from '../labels/empty-field-label';
import ErrorLabel from '../labels/error-label';
import FieldLabel from '../labels/field-label';
import { buildBaseExtensions } from './extensions';
import { handleImageDrop } from './image/image-drop-handler';
import { handleImagePaste } from './image/image-paste-handler';
import { ImageSizeBadge } from './image/image-size-badge';
import { isEmptyTiptapHtml } from './is-empty-content';
import { sanitizePastedHTML } from './paste-sanitizer';
import { TableCellMenu } from './table/table-cell-menu';
import { Toolbar } from './toolbar';
import { useMaximize } from './use-maximize';
import './rich-text.css';
import { mergeStyling } from '../common-functions';

// Astral-plane code points need 4-byte UTF-8. The backend (and
// downstream email charsets) doesn't always preserve them, so they reach inboxes as `?`.
// Encode them as HTML numeric entities so the bytes on the wire are pure ASCII; the
// editor's parser decodes them back to glyphs on the way in.
const encodeAstralForEmail = (html: string): string =>
  html.replace(/[\u{10000}-\u{10FFFF}]/gu, (ch) => `&#${ch.codePointAt(0)};`);

export interface RichTextAreaProps {
  content?: string;
  onChange?: (html: string) => void;
  /** Fires when the editor loses focus. Form wrappers use this to mark the field touched. */
  onBlur?: () => void;
  editorRef?: (editor: Editor | null) => void;
  placeholder?: string;
  className?: string;
  minHeight?: number;
  /**
   * Caps the editor body height. When content exceeds this, the editor scrolls internally
   * rather than growing the page. The sticky toolbar stays pinned to the top of the body so its buttons remain reachable.
   *
   * @default 480
   */
  maxHeight?: number;
  /** When true, renders the editor with the error color border. Form wrappers set this. */
  invalid?: boolean;
  /** When provided, renders a label row above the editor with an optional required asterisk. */
  label?: string;
  labelStyles?: string;
  required?: DynamicFormRequiredType;
  /** When true, renders the content read-only via `InnerHTMLDiv` instead of mounting an editor. */
  readOnly?: boolean;
  /** When provided, renders an `ErrorLabel` below the editor and applies invalid styling. */
  error?: string;
}

export const RichTextArea = ({
  content,
  onChange,
  onBlur,
  editorRef,
  placeholder,
  className,
  minHeight = 120,
  maxHeight = 480,
  invalid,
  label,
  labelStyles,
  required,
  readOnly,
  error,
}: RichTextAreaProps) => {
  const showError = error !== undefined && error.length > 0;
  const isInvalid = invalid || showError;

  return (
    <div>
      {label && (
        <div className="flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly ? (
        isEmptyTiptapHtml(content) ? (
          <EmptyFieldLabel />
        ) : (
          <InnerHTMLDiv html={content ?? ''} className={className} />
        )
      ) : (
        <EditorBody
          content={content}
          onChange={onChange}
          onBlur={onBlur}
          editorRef={editorRef}
          placeholder={placeholder}
          className={className}
          minHeight={minHeight}
          maxHeight={maxHeight}
          invalid={isInvalid}
        />
      )}
      {showError && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};

interface EditorBodyProps {
  content?: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  editorRef?: (editor: Editor | null) => void;
  placeholder?: string;
  className?: string;
  minHeight: number;
  maxHeight: number;
  invalid?: boolean;
}

const EditorBody = ({
  content,
  onChange,
  onBlur,
  editorRef,
  placeholder,
  className,
  minHeight,
  maxHeight,
  invalid,
}: EditorBodyProps) => {
  // Keep the latest onChange / onBlur in refs so the editor callbacks stay stable.
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);

  // FileReader callbacks in handlePaste may fire after this component unmounts; check
  // this flag before dispatching to avoid acting on a destroyed editor view.
  const editorInstanceRef = useRef<Editor | null>(null);

  // Extensions are immutable after editor creation; memoize so the array reference is stable.
  const extensions = useMemo(() => buildBaseExtensions({ placeholder }), [placeholder]);

  const editor = useEditor({
    extensions,
    content: content ?? '',
    // Descendants subscribe to derived state via useEditorState; suppress the wholesale
    // useEditor re-render-on-transaction (Tiptap performance guide).
    shouldRerenderOnTransaction: false,
    // Use a single newline between blocks when serialising to plain text for the clipboard
    // (default is two - that's Markdown convention but produces unexpected double-spacing when
    // users copy from the editor into a plain text field).
    coreExtensionOptions: {
      clipboardTextSerializer: {
        blockSeparator: '\n',
      },
    },
    editorProps: {
      transformPastedHTML: sanitizePastedHTML,
      handlePaste: (view, event) =>
        handleImagePaste(view, event, () => {
          const instance = editorInstanceRef.current;
          return instance !== null && !instance.isDestroyed;
        }),
      handleDrop: (view, event, slice, moved) =>
        handleImageDrop(
          view,
          event,
          () => {
            const instance = editorInstanceRef.current;
            return instance !== null && !instance.isDestroyed;
          },
          slice,
          moved
        ),
    },
    onUpdate: ({ editor: e }) => {
      onChangeRef.current?.(encodeAstralForEmail(e.getHTML()));
    },
    onBlur: () => {
      onBlurRef.current?.();
    },
  });

  useEffect(() => {
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
    editorInstanceRef.current = editor;
  });

  useEffect(() => {
    editorRef?.(editor);
    return () => editorRef?.(null);
  }, [editor, editorRef]);

  // Sync external content changes while the user is not editing.
  useEffect(() => {
    if (!editor) {
      return;
    }

    if (editor.isFocused) {
      return;
    }

    // Compare on the encoded form since stored content carries entities while the live
    // editor holds raw glyphs - otherwise we'd re-setContent on every emit.
    const current = encodeAstralForEmail(editor.getHTML());
    const next = content ?? '';
    if (current !== next) {
      // Non-chain: avoid the focus/scroll side effects chain() would apply when the user is elsewhere.
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [content, editor]);

  const { maximized, toggleMaximize, closeMaximize, dialogRef, inlineRef, originRectRef } = useMaximize(editor);

  if (!editor) {
    return null;
  }

  const containerClassName = mergeStyling(
    'tiptap tiptap-editor',
    { 'is-invalid': invalid, 'is-maximized': maximized },
    className
  );

  const container = (editor.view.dom.closest('dialog, [role="dialog"]') as HTMLElement) ?? undefined;

  const body = (
    <>
      <Toolbar maximized={maximized} onToggleMaximize={toggleMaximize} />
      <EditorContent
        editor={editor}
        className="flex h-full flex-1 flex-col"
        // Drop the height cap when maximized so the body fills the viewport instead of the inline maxHeight.
        style={{ minHeight: `${minHeight}px`, maxHeight: maximized ? undefined : `${maxHeight}px` }}
      />
      <ImageSizeBadge />
      <TableCellMenu container={maximized ? dialogRef : container} />
    </>
  );

  return (
    <EditorContext.Provider value={{ editor }}>
      {maximized ? (
        <>
          <dialog ref={dialogRef} className={containerClassName} onClose={closeMaximize}>
            {body}
          </dialog>
          {/* Holds the editor's layout slot at its captured inline height while it's in the modal, so the form doesn't collapse. */}
          {originRectRef.current && <div aria-hidden style={{ height: originRectRef.current.height }} />}
        </>
      ) : (
        <div ref={inlineRef} className={containerClassName}>
          {body}
        </div>
      )}
    </EditorContext.Provider>
  );
};
